import {
  defaultPurchaseOrderTransferDestinationPublicLocationId,
  defaultPurchaseOrderTransferOriginPublicLocationId,
  type HubPurchaseOrderStatus,
  type MrpPurchaseOrderLine,
  type MrpPurchaseOrderLinkedTransferRequest,
  type MrpTransferReason,
} from "@/lib/mrp";
import { normalizePurchaseOrderSku } from "@/lib/purchaseOrderLineGroups";
import {
  emptyMvLine,
  newDraftLineClientId,
  sanitizeMvPoLineForms,
  formatSavedUnitPrice,
  type MvPoLineForm,
  type MvPoLineTransferRequestForm,
} from "@/lib/purchaseOrderDraftForm";
import { trimText } from "@/lib/utils";

export function linkedTransferRequestToForm(
  row: MrpPurchaseOrderLinkedTransferRequest,
): MvPoLineTransferRequestForm {
  return {
    client_id: row.id,
    quantity: String(row.requested_quantity),
    destination_location_id: String(row.destination_location_id),
    target_date: row.target_date,
    reason: (row.reason === "upcoming_needs" ? "upcoming_needs" : "upcoming_needs") satisfies MrpTransferReason,
    notes: row.notes ?? "",
  };
}

function attachLinkedTransfersToLines(
  rows: MvPoLineForm[],
  linkedTransferRequests: MrpPurchaseOrderLinkedTransferRequest[],
): MvPoLineForm[] {
  const transfersByItemId = new Map<number, MvPoLineTransferRequestForm[]>();
  const legacyTransfersBySku = new Map<string, MvPoLineTransferRequestForm[]>();

  for (const transfer of linkedTransferRequests) {
    const form = linkedTransferRequestToForm(transfer);
    const itemId = transfer.source_public_purchase_order_item_id;
    if (itemId != null && Number.isFinite(Number(itemId))) {
      const normalizedItemId = Number(itemId);
      const list = transfersByItemId.get(normalizedItemId) ?? [];
      list.push(form);
      transfersByItemId.set(normalizedItemId, list);
      continue;
    }
    const key = normalizePurchaseOrderSku(transfer.sku_id);
    if (key === "") continue;
    const list = legacyTransfersBySku.get(key) ?? [];
    list.push(form);
    legacyTransfersBySku.set(key, list);
  }

  const seenLegacySku = new Set<string>();
  return rows.map((row) => {
    if (row.is_delivery_segment) {
      return row;
    }
    const itemId = row.purchase_order_item_id;
    if (itemId != null && transfersByItemId.has(itemId)) {
      return { ...row, transfer_requests: transfersByItemId.get(itemId) ?? [] };
    }
    const skuKey = normalizePurchaseOrderSku(row.sku);
    if (skuKey === "" || seenLegacySku.has(skuKey)) {
      return { ...row, transfer_requests: [] };
    }
    seenLegacySku.add(skuKey);
    return { ...row, transfer_requests: legacyTransfersBySku.get(skuKey) ?? [] };
  });
}

/** Flat display rows for a saved Megaventory PO — same shape as draft lines, including delivery splits. */
export function savedPurchaseOrderLinesToDisplayForm(
  lines: MrpPurchaseOrderLine[],
  locationCode: string,
  linkedTransferRequests: MrpPurchaseOrderLinkedTransferRequest[],
): MvPoLineForm[] {
  if (lines.length === 0) {
    return [];
  }

  const rows: MvPoLineForm[] = [];
  for (const line of lines) {
    const segments = line.delivery_segments ?? [];
    const [firstSegment, ...restSegments] = segments;
    const unitPrice = line.unit_price == null ? "" : formatSavedUnitPrice(line.unit_price);
    const mvRowDetailId = line.mv_row_detail_id != null ? String(line.mv_row_detail_id) : "";

    // Same shape as draft commercial splits: the first delivery segment IS the top
    // row (quantity + date), remaining segments render as indented sibling rows.
    rows.push({
      ...emptyMvLine(locationCode),
      client_id: `po-item-${String(line.id)}`,
      purchase_order_item_id: line.id,
      delivery_segment_id: firstSegment?.id,
      sku: trimText(line.cpn),
      line_description: trimText(line.description),
      quantity: String(firstSegment != null ? firstSegment.quantity : line.quantity_ordered),
      unit_price: unitPrice,
      mv_row_detail_id: mvRowDetailId,
      expected_delivery_date:
        firstSegment != null
          ? trimText(firstSegment.expected_arrival)
          : trimText(line.expected_arrival),
      quantity_received: line.quantity_received,
      quantity_invoiced: line.quantity_invoiced,
      quantity_invoiced_total: line.quantity_invoiced_total,
      receiving_status: line.status,
      sku_catalog_description: line.sku_catalog_description,
      mv_unit: line.mv_unit ?? null,
      transfer_requests: [],
    });

    for (const segment of restSegments) {
      rows.push({
        ...emptyMvLine(locationCode),
        client_id: `po-delivery-${String(segment.id)}`,
        purchase_order_item_id: line.id,
        delivery_segment_id: segment.id,
        is_delivery_segment: true,
        sku: trimText(line.cpn),
        line_description: trimText(line.description),
        quantity: String(segment.quantity),
        unit_price: unitPrice,
        mv_row_detail_id: mvRowDetailId,
        expected_delivery_date: trimText(segment.expected_arrival),
        quantity_received: line.quantity_received,
        quantity_invoiced: line.quantity_invoiced,
        quantity_invoiced_total: line.quantity_invoiced_total,
        receiving_status: line.status,
        mv_unit: line.mv_unit ?? null,
        transfer_requests: [],
      });
    }
  }

  return sanitizeMvPoLineForms(attachLinkedTransfersToLines(rows, linkedTransferRequests));
}

export function defaultSavedTransferRequestBody(
  sku: string,
  locationCode: string,
  openQuantity: number,
  targetDate: string,
  purchaseOrderItemId: number | null,
): {
  cpn: string;
  purchase_order_item_id?: number;
  quantity: number;
  origin_location_id: number;
  destination_location_id: number;
  target_date: string;
  notes: null;
  reason: "upcoming_needs";
} {
  return {
    cpn: trimText(sku),
    ...(purchaseOrderItemId != null ? { purchase_order_item_id: purchaseOrderItemId } : {}),
    quantity: Math.max(1, Math.round(openQuantity)),
    origin_location_id: defaultPurchaseOrderTransferOriginPublicLocationId(locationCode),
    destination_location_id: defaultPurchaseOrderTransferDestinationPublicLocationId(locationCode),
    target_date: trimText(targetDate) !== "" ? trimText(targetDate) : new Date().toISOString().slice(0, 10),
    notes: null,
    reason: "upcoming_needs",
  };
}

export function newSavedTransferRequestClientId(): string {
  return newDraftLineClientId();
}

export function purchaseOrderSavedLineCommercialFieldsEditable(input: {
  hubWritesAllowed: boolean;
  isHubOwned: boolean;
  hubStatus: HubPurchaseOrderStatus | null;
  savedMvStatus: string;
}): boolean {
  if (!input.hubWritesAllowed) {
    return false;
  }
  if (input.isHubOwned) {
    return input.hubStatus === "draft" || input.hubStatus === "ordered";
  }
  return input.savedMvStatus === "pending" || input.savedMvStatus === "verified";
}
